import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { AccessToken, IUserGoalPreferences, IUserPersonalInfo, IUserSessionData } from '../models/user.model';
import { catchError, map } from 'rxjs/operators';
import { IStepConfirmValidation, RegisterReceiptData } from '../models/register.model';
import { BillingRedirectError } from 'app/errors/billing-redirect.error';
import { ConfirmationRedirectError } from 'app/errors/confirmation-redirect.error';
import { IApiModel, IListApiModel } from '../models/api.model';

import { environment } from '../../../environments/environment';
import { IAccount } from '../models/account.model';
import { EDeletePeriod } from 'app/dialogs/manage-deleted-account/manage-deleted-account.component';

@Injectable()
export class UserService {
  constructor(private http: HttpClient) {
  }

  confirm(token: string): Observable<AccessToken | null> {
    return this.http
      .put<AccessToken | null>(environment.apiUrl + `/v1/users/confirm`, {
        confirmation_token: token,
      })
      .pipe(
        map((result: AccessToken | null) => {
          if (result) {
            localStorage.setItem('token', JSON.stringify(result));
            localStorage.setItem('comming_from', 'verify');
          }

          return result;
        }),
      );
  }

  acceptPolicy(idAccount): Observable<IApiModel> {
    return this.http.post<IApiModel>(environment.apiUrl + `/v1/users/policy`, {
      account_id: idAccount,
    });
  }

  logout(performReturn: boolean = false): Observable<AccessToken | null> {
    return this.http.get<AccessToken | null>(
      environment.apiUrl + '/v1/users/logout',
      {
        params: {
          return_original: performReturn.toString(),
        },
      },
    );
  }

  login({ username, password }): Observable<AccessToken | null> {
    return this.http
      .post<any>(environment.apiUrl + `/v1/users/login`, {
        email: username,
        password,
      })
      .pipe(
        map(response => {
          if (response) {
            localStorage.setItem('token', JSON.stringify(response));
            return response;
          }

          return null;
        }),
        catchError(error => {
          if (
            error instanceof HttpErrorResponse &&
            error.status === 400 &&
            [1002, 1003].indexOf(error.error.code) !== -1
          ) {
            if (error.error.code === 1002) {
              return throwError(new BillingRedirectError());
            } else if (error.error.code === 1003) {
              return throwError(new ConfirmationRedirectError());
            }
          }

          return throwError(error);
        }),
      );
  }

  validateEmailAddress(data): Observable<IStepConfirmValidation> {
    return this.http.post<any>(environment.apiUrl + `/v1/users/register`, {
      ...data,
      validate_only: true,
    });
  }

  payFee(userData, data): Observable<RegisterReceiptData> {
    if (userData === null) {
      return this.http.post<any>(environment.apiUrl + '/v1/users/payment', {
        ...data,
      });
    }

    return this.http.post<any>(environment.apiUrl + `/v1/users/register`, {
      ...userData,
      ...data,
    });
  }

  getSessionData(): Observable<IUserSessionData> {
    return this.http.get<IUserSessionData>(
      environment.apiUrl + '/v1/users/session',
    );
  }

  changePassword(
    accountId: number,
    oldPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Observable<any> {
    return this.http.put<any>(environment.apiUrl + '/v1/users/password', {
      account_id: accountId,
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  updateGoalPreferences(
    accountId: number,
    prefs: IUserGoalPreferences,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      ...prefs,
    });
  }

  updateUserTimezone(
    accountId: number,
    timezone: string,
    observeDst: boolean,
  ): Observable<{
    settings: number;
  }> {
    return this.http.put<{
      settings: number;
    }>(environment.apiUrl + `/v1/settings/${accountId}`, {
      time_zone: timezone,
      dst: observeDst,
    });
  }

  updateUserSmsDetails(
    accountId: number,
    sendSms: boolean, mobileNumber: string
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      send_sms: sendSms,
      mobile_phone: mobileNumber
    });
  }

  updateUserPersonalInfo(
    accountId: number,
    info: IUserPersonalInfo,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      ...info,
    });
  }

  toggleEmailPreview(
    accountId,
    currentValue,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      emails_preview: !currentValue,
    });
  }

  toggleHints(
    accountId: number,
    value: boolean,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/users/' + accountId, {
      popup_tips: value,
    });
  }

  toggleVipFeature(
    accountId: number,
    value: boolean,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/settings/' + accountId, {
      vip_mode: value,
    });
  }

  toggleUnsubscribe(
    accountId: number,
    state: boolean,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/settings/' + accountId, {
      unsubscribe: state,
    });
  }

  getLabels(): Observable<any> {
    return this.http.get<any>(environment.apiUrl + '/v1/users/view-labels');
  }

  updateLabels(data): Observable<any> {
    return this.http.post<any>(environment.apiUrl + `/v1/users/manage-labels`, {
      ...data,
    });
  }

  getBillingInfo(accountId: number): Observable<any> {
    return this.http.get<any>(environment.apiUrl + `/v1/users/${accountId}`);
  }

  getAccountInf(accountId): Observable<any> {
    return this.http.get<any>(environment.apiUrl + `/v1/users/${accountId}`);
  }

  updateBillingInfo(accountId, data): Observable<any> {
    return this.http.put<any>(environment.apiUrl + `/v1/users/${accountId}`, {
      ...data,
    });
  }

  updateAccountPayment(accountId: number, paymentData: any): Observable<{ id_user: number }> {
    return this.http.put<{ id_user: number }>(environment.apiUrl + `/v1/users/${accountId}/payment`, paymentData);
  }

  getPaymentMethods(accountId: number): Observable<{
    default_payment_method_id: string | null;
    items: Array<{
      id: string;
      is_default: boolean;
      summary: Record<string, unknown>;
    }>;
  }> {
    return this.http.get<{
      default_payment_method_id: string | null;
      items: Array<{
        id: string;
        is_default: boolean;
        summary: Record<string, unknown>;
      }>;
    }>(environment.apiUrl + `/v1/users/${accountId}/payment-methods`);
  }

  setDefaultPaymentMethod(
    accountId: number,
    paymentMethodId: string,
  ): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      environment.apiUrl + `/v1/users/${accountId}/payment-methods/default`,
      { payment_method_id: paymentMethodId },
    );
  }

  /** Payment Element: create SetupIntent for an authenticated user (add/change default payment method). */
  createSetupIntent(accountId: number): Observable<{
    client_secret: string;
    setup_intent_id: string;
    stripe_publishable_key: string;
  }> {
    return this.http.post<{
      client_secret: string;
      setup_intent_id: string;
      stripe_publishable_key: string;
    }>(environment.apiUrl + `/v1/users/${accountId}/setup-intent`, {});
  }

  /**
   * Registration step 1: create account + Stripe customer + SetupIntent (no card in API body).
   */
  registerPaymentElementInit(body: Record<string, any>): Observable<{
    success: boolean;
    account_id: number;
    client_secret: string;
    setup_intent_id: string;
    stripe_publishable_key: string;
  }> {
    const { use_payment_element: _u, terms_accepted: termsIn, ...rest } = body;
    return this.http.post<{
      success: boolean;
      account_id: number;
      client_secret: string;
      setup_intent_id: string;
      stripe_publishable_key: string;
    }>(environment.apiUrl + '/v1/users/register', {
      ...rest,
      use_payment_element: true,
      terms_accepted: termsIn !== undefined ? termsIn : true,
    });
  }

  registerPaymentElementComplete(body: {
    account_id: number;
    setup_intent_id: string;
    plan_id: number;
    discount?: string;
  }): Observable<any> {
    return this.http.post(environment.apiUrl + '/v1/users/register/complete-setup', body);
  }

  deleteUser(accountId: number, forward?: boolean, period?: EDeletePeriod | number): Observable<any> {
    const params: any = {};
    if (forward !== undefined) {
      params.deleted_forward = forward;
    }
    if (period !== undefined) {
      params.period = period;
    }
    return this.http.delete<any>(environment.apiUrl + `/v1/users/${accountId}`, { 
      body: params
     });
  }

  resetPassword(
    accountId: number,
    newPassword: string,
    confirmPassword: string,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/users/password', {
      account_id: accountId,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  setNotificationsEmail(
    accountId: number,
    email: string,
    notification: boolean = false,
    forward: boolean = false,
  ): Observable<{
    success: boolean;
  }> {
    return this.http.post<{
      success: boolean;
    }>(environment.apiUrl + '/v1/users/notification_email', {
      account_id: accountId,
      email,
      notification,
      forward,
    });
  }

  getVsmUsers(): Observable<IListApiModel<IAccount>> {
    return this.http.get<IListApiModel<IAccount>>(
      environment.apiUrl + '/v1/users',
      {
        params: {
          user_type: 'vsm',
        },
      },
    );
  }

  setForwarding(accountId: number, forward: boolean, period: number): Observable<{success: boolean}>{
    return this.http.put<{success: boolean}>(environment.apiUrl + `/v1/users/forward`, {
      forward,
      period,
      account_id: accountId
    });
  }
}
